import { UI_Grid } from 'ui/components/4-habitats/Grid/Grid';
import { UI_GridCell } from 'ui/components/4-habitats/Grid/GridCell';
import { MultiSelectProps, SubOption, UI_MultiSelect } from 'ui/components/3-organisms/MultiSelect/MultiSelect';
import { useDictionaryContext } from '3-foundation/Dictionary/DictionaryContext';

interface Props {
	filters: {
		subjectTags: (Site.SiteTagFolder | Site.SiteTag)[];
		statusTags: (Site.SiteTagFolder | Site.SiteTag)[];
		typeTags: (Site.SiteTagFolder | Site.SiteTag)[];
		dateTags: Content.DocumentFacet[];
	};
	labels: {
		dateFilter?: string;
		subjectFilter?: string;
		statusFilter?: string;
		typeFilter?: string;
	};
	selectedFilters: {
		dateTags: string[];
		subjectTags: string[];
		statusTags: string[];
		typeTags: string[];
	};
	handleFilterChange: (group: 'dateTags' | 'subjectTags' | 'statusTags' | 'typeTags', selectedValues: string[]) => void;
}

export const DocumentFilters = ({ filters, labels, selectedFilters, handleFilterChange }: Props) => {
	const dictionary = useDictionaryContext();

	const textsWithoutLabel = {
		placeholder: dictionary.getLabel('DropdownPlaceholder'),
		selected: dictionary.getLabel('Selected'),
	};

	const convertDateFacetsToOptions = (facets: Content.DocumentFacet[]): MultiSelectProps['options'] => {
		if (!facets) return;
		const groupedByYear = new Map<string, SubOption[]>();

		facets.forEach((facet) => {
			const [year, month] = facet.name.split('-');
			if (!groupedByYear.has(year)) groupedByYear.set(year, []);
			groupedByYear.get(year)!.push({
				label: month,
				value: facet.name,
			});
		});
		return Array.from(groupedByYear.entries()).map(([year, subOptions]) => ({
			label: year,
			value: year,
			subOptions,
		}));
	};

	const convertFiltersToOptions = (optionFilters: Site.SiteTag[] | Site.SiteTagFolder[]): MultiSelectProps['options'] => {
		return optionFilters?.map((group: Site.SiteTag | Site.SiteTagFolder) => {
			if ('children' in group && Array.isArray(group.children)) {
				return {
					label: group.name,
					value: group.name,
					subOptions: group.children.map((child) => ({
						label: child.name,
						value: child.name,
					})),
				};
			}
			return { label: group.name, value: group.name };
		});
	};

	return (
		<UI_Grid wrap container>
			{labels.dateFilter && filters?.dateTags && (
				<UI_GridCell desktopWidth="25">
					<UI_MultiSelect
						id="date-filters"
						onChange={(values) => handleFilterChange('dateTags', values)}
						options={convertDateFacetsToOptions(filters?.dateTags)}
						texts={{ label: labels.dateFilter, ...textsWithoutLabel }}
						value={selectedFilters.dateTags}
					/>
				</UI_GridCell>
			)}

			{labels.subjectFilter && filters?.subjectTags && (
				<UI_GridCell desktopWidth="25">
					<UI_MultiSelect
						id="subject-filters"
						onChange={(values) => handleFilterChange('subjectTags', values)}
						options={convertFiltersToOptions(filters?.subjectTags)}
						texts={{ label: labels.subjectFilter, ...textsWithoutLabel }}
						value={selectedFilters.subjectTags}
					/>
				</UI_GridCell>
			)}

			{labels.statusFilter && filters?.statusTags && (
				<UI_GridCell desktopWidth="25">
					<UI_MultiSelect
						id="status-filters"
						onChange={(values) => handleFilterChange('statusTags', values)}
						options={convertFiltersToOptions(filters?.statusTags)}
						texts={{ label: labels.statusFilter, ...textsWithoutLabel }}
						value={selectedFilters.statusTags}
					/>
				</UI_GridCell>
			)}

			{labels.typeFilter && filters?.typeTags && (
				<UI_GridCell desktopWidth="25">
					<UI_MultiSelect
						id="type-filters"
						onChange={(values) => handleFilterChange('typeTags', values)}
						options={convertFiltersToOptions(filters?.typeTags)}
						texts={{ label: labels.typeFilter, ...textsWithoutLabel }}
						value={selectedFilters.typeTags}
						className="u-bottom-margin--xl"
					/>
				</UI_GridCell>
			)}
		</UI_Grid>
	);
};
